<script setup lang="ts">
import AssistantModalHeader from '@/components/assistant/desktop/AssistantModalHeader.vue'
import ChatArea from '@/components/assistant/chat/ChatArea.vue'
import ConversationList from '@/components/assistant/ConversationList.vue'
import CreditUsageVue from '@/components/assistant/CreditUsage.vue'
import SidebarIcon from '@/components/assistant/SidebarIcon.vue'
import SquaredIconButton from '@/components/assistant/SquaredIconButton.vue'
import type {ConversationIntent} from '@/components/assistant/types'
import type {Conversation} from '@/client/mentor/ConversationsClient'
import type {CreditUsage} from '@/client/mentor/CreditUsageClient'
import {
  DESKTOP_PADDINGX_PX,
  MAX_WIDTH_PX,
  MOBILE_BREAKPOINT_PX,
  HEADER_HEIGHT_PX,
  SIDEBAR_WIDTH_PX,
} from '@/components/assistant/constants'
import {computed, ref} from 'vue'
import {useAssistantPrint} from '@/components/assistant/composables/useAssistantPrint'
import {useAssistantStore} from '@/stores/assistant'
import {useI18n} from 'vue-i18n'
import {useStorage} from '@vueuse/core'

const assistantStore = useAssistantStore()
const assistantDesktopSidebarOpen = useStorage('assistant-desktop-sidebar-open', false)
const assistantDesktopExpanded = useStorage('assistant-desktop-expanded', false)
const {t} = useI18n()
const {printConversation} = useAssistantPrint()

defineProps<{
  companyId: number
  creditUsage?: CreditUsage
  conversations: Conversation[]
  hasNextPage: boolean
  isFetchingNextPage: boolean
  intent: ConversationIntent
}>()

defineEmits<{
  pendingMessageResolved: []
}>()

const isSidebarClosing = ref(false)

const showSidebarToggle = computed(() => !assistantDesktopSidebarOpen.value && !isSidebarClosing.value)

const backdropStyles = computed(() => ({
  top: `${HEADER_HEIGHT_PX}px`,
  height: `calc(100vh - ${HEADER_HEIGHT_PX}px)`,
}))

const modalStyles = computed(() => ({
  position: 'absolute' as const,
  top: `calc(${HEADER_HEIGHT_PX}px + ${DESKTOP_PADDINGX_PX}px)`,
  right: `${DESKTOP_PADDINGX_PX}px`,
  height: `calc(100vh - ${HEADER_HEIGHT_PX}px - ${2 * DESKTOP_PADDINGX_PX}px)`,
  width: assistantDesktopExpanded.value ? `calc(100vw - ${2 * DESKTOP_PADDINGX_PX}px)` : '80%',
  minWidth: `${MOBILE_BREAKPOINT_PX}px`,
  maxWidth: assistantDesktopExpanded.value ? 'none' : `${MAX_WIDTH_PX}px`,
}))
</script>

<template>
  <!-- Dim background -->
  <div class="fixed left-0 w-screen bg-slate-500/50 z-assistant-backdrop text-base print:hidden" :style="backdropStyles"></div>

  <!-- Modal -->
  <div
    class="flex flex-row overflow-hidden bg-white rounded-md shadow-md z-assistant text-base assistant-modal-print"
    :style="modalStyles"
  >
    <!-- Sidebar -->
    <Transition
      name="assistant-sidebar-slide"
      @after-enter="isSidebarClosing = false"
      @after-leave="isSidebarClosing = false"
      @before-enter="isSidebarClosing = false"
      @before-leave="isSidebarClosing = true"
    >
      <div
        v-if="assistantDesktopSidebarOpen"
        class="h-full border-r-[1px] flex flex-col overflow-x-hidden shrink-0 print:hidden"
        :style="{width: `${SIDEBAR_WIDTH_PX}px`}"
      >
        <!-- Header -->
        <div class="h-10 px-3 shrink-0 flex flex-row items-center gap-3 border-b-[1px]">
          <button @click="assistantDesktopSidebarOpen = !assistantDesktopSidebarOpen">
            <SidebarIcon />
          </button>

          <span class="font-semi-bold">{{ t('assistant.conversations') }}</span>

          <SquaredIconButton
            v-tt="$t('assistant.actions.newChat')"
            class="ml-auto"
            :disabled="!assistantStore.activeConversationId"
            icon-classes="fa-solid fa-plus"
            @click="assistantStore.setActiveConversation(undefined)"
          />
        </div>

        <!-- Body -->
        <ConversationList
          :conversations="conversations"
          :has-next-page="hasNextPage"
          :intent="intent"
          :is-fetching-next-page="isFetchingNextPage"
        />

        <div class="p-3">
          <CreditUsageVue :credit-usage="creditUsage" />
        </div>
      </div>
    </Transition>

    <!-- Chat Area -->
    <div class="flex-1 min-w-0 min-h-0 flex flex-col">
      <AssistantModalHeader
        :is-expanded="assistantDesktopExpanded"
        :is-sidebar-open="assistantDesktopSidebarOpen"
        :show-sidebar-toggle="showSidebarToggle"
        @print="printConversation()"
        @toggle-expand="assistantDesktopExpanded = !assistantDesktopExpanded"
        @toggle-sidebar="assistantDesktopSidebarOpen = !assistantDesktopSidebarOpen"
      />
      <ChatArea
        :company-id="companyId"
        :credit-usage="creditUsage"
        :intent="intent"
        @pending-message-resolved="$emit('pendingMessageResolved')"
      />
    </div>
  </div>
</template>

<style scoped>
.assistant-sidebar-slide-enter-active,
.assistant-sidebar-slide-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.assistant-sidebar-slide-enter-from,
.assistant-sidebar-slide-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}
</style>
